/**
 * Iter135 · Diálogo de entrevista técnica de minería subterránea.
 *
 * Al abrir un candidato desde el pipeline (kanban de vacantes), el
 * reclutador elige el rol (director, gerente, supervisor, operador…),
 * el sistema carga la plantilla con preguntas técnicas + situacionales
 * + safety + regulación, y captura las respuestas + puntaje. El
 * resultado se guarda como `interview_notes` enriquecida.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardText, Star, FloppyDisk, CheckCircle, ChatCircleDots,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RECOMMENDATIONS = [
  { v: "hire",     l: "Contratar",        bg: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  { v: "consider", l: "Considerar",       bg: "bg-amber-100 text-amber-900 border-amber-300" },
  { v: "reject",   l: "No recomendar",    bg: "bg-red-100 text-red-900 border-red-300" },
];

function QuestionInput({ q, value, onChange, score, onScore }) {
  const val = value || "";
  if (q.kind === "text") {
    return (
      <Textarea rows={2} value={val} onChange={(e) => onChange(e.target.value)}
                 data-testid={`iv-q-${q.id}`} className="text-xs" />
    );
  }
  if (q.kind === "short") {
    return (
      <Input value={val} onChange={(e) => onChange(e.target.value)}
              data-testid={`iv-q-${q.id}`} className="h-9 text-xs" />
    );
  }
  if (q.kind === "yes_no") {
    return (
      <div className="flex gap-2">
        {["sí", "no"].map((v) => (
          <button key={v} type="button"
                   onClick={() => onChange(v)}
                   data-testid={`iv-q-${q.id}-${v}`}
                   className={`px-3 py-1.5 text-xs rounded border ${
                     val === v ? "bg-blue-900 text-white border-blue-900"
                                 : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                   }`}>{v.toUpperCase()}</button>
        ))}
        <Input value={val && val !== "sí" && val !== "no" ? val : ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Justificación…" className="h-8 text-xs flex-1" />
      </div>
    );
  }
  if (q.kind === "scale_1_5") {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => { onChange(String(n)); onScore?.(n); }}
                   data-testid={`iv-q-${q.id}-${n}`}
                   className={`w-9 h-9 rounded font-bold text-sm border ${
                     val === String(n) ? "bg-amber-400 text-white border-amber-500"
                                          : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                   }`}>{n}</button>
        ))}
      </div>
    );
  }
  if (q.kind && q.kind.startsWith("options:")) {
    const opts = q.kind.slice("options:".length).split("|");
    return (
      <Select value={val} onValueChange={onChange}>
        <SelectTrigger className="h-9 text-xs" data-testid={`iv-q-${q.id}`}>
          <SelectValue placeholder="Elige…" />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {opts.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
        </SelectContent>
      </Select>
    );
  }
  return null;
}

export default function InterviewDialog({ candidate, open, onClose, onSaved }) {
  const [templates, setTemplates] = useState(null);
  const [slug, setSlug] = useState("");
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState({});
  const [sectionScores, setSectionScores] = useState({});
  const [recommendation, setRecommendation] = useState("");
  const [comments, setComments] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/hr/interview-templates").then((r) => setTemplates(r.data))
        .catch((e) => toast.error(apiError(e)));
  }, [open]);

  const loadTemplate = useCallback(async (s) => {
    setSlug(s); setTpl(null); setAnswers({}); setSectionScores({});
    if (!s) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/hr/interview-templates/${s}`);
      setTpl(data);
      const initScores = {};
      (data.sections || []).forEach((sec, i) => { initScores[i] = 3; });
      setSectionScores(initScores);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  const overallScore = useMemo(() => {
    if (!tpl) return 0;
    let total = 0, weight = 0;
    (tpl.sections || []).forEach((sec, i) => {
      const w = Number(sec.weight || 10);
      const sc = Number(sectionScores[i] || 0);        // 1-5
      total += (sc / 5) * 100 * w;
      weight += w;
    });
    return weight ? Number((total / weight).toFixed(1)) : 0;
  }, [tpl, sectionScores]);

  const save = async () => {
    if (!slug) { toast.error("Elige un rol"); return; }
    if (!recommendation) { toast.error("Marca una recomendación"); return; }
    setSaving(true);
    try {
      await api.post(`/hr/candidates/${candidate.id}/interview`, {
        template_slug: slug,
        answers, overall_score: overallScore,
        recommendation, comments,
      });
      toast.success(`Entrevista guardada · ${overallScore}/100`);
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: { value } }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto"
                      data-testid="interview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardText size={18} weight="fill" className="text-indigo-800" />
            Entrevista técnica · {candidate?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mb-2">
          <div>
            <Label className="overline-label">Rol / puesto a entrevistar</Label>
            <Select value={slug} onValueChange={loadTemplate}>
              <SelectTrigger className="h-10" data-testid="iv-role-select">
                <SelectValue placeholder="Elige el rol…" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-96 overflow-y-auto">
                {templates && Object.entries(templates.categories || {}).map(([cat, list]) => (
                  <div key={cat}>
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase font-bold text-slate-400 tracking-widest">
                      {cat}
                    </div>
                    {list.map((r) => (
                      <SelectItem key={r.slug} value={r.slug}>{r.label}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && <p className="text-xs text-slate-400 italic">Cargando plantilla…</p>}

        {tpl && (
          <>
            <div className="bg-indigo-50 border border-indigo-200 rounded p-3 mb-3">
              <div className="flex items-center gap-2">
                <Star size={16} weight="fill" className="text-indigo-700" />
                <span className="font-bold text-indigo-900 text-sm">Puntaje ponderado: {overallScore}/100</span>
                <span className="ml-auto text-[10px] text-indigo-700 italic">
                  Escala 1-5 por sección · ponderación en cada bloque
                </span>
              </div>
            </div>

            {(tpl.sections || []).map((sec, si) => (
              <div key={si} className="border border-slate-200 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-slate-900 text-sm">{sec.title}</h4>
                  <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">peso {sec.weight}%</span>
                  <div className="ml-auto flex items-center gap-1">
                    <span className="text-[10px] text-slate-500">Puntaje sección:</span>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button"
                               data-testid={`iv-sec-${si}-score-${n}`}
                               onClick={() => setSectionScores((p) => ({ ...p, [si]: n }))}
                               className={`w-7 h-7 rounded text-xs font-bold border ${
                                 sectionScores[si] === n
                                   ? "bg-amber-400 text-white border-amber-500"
                                   : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                               }`}>{n}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  {(sec.questions || []).map((q) => (
                    <div key={q.id}>
                      <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                        {q.prompt}
                      </Label>
                      <QuestionInput q={q} value={answers[q.id]?.value}
                                      onChange={(v) => setAnswer(q.id, v)} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="border border-slate-200 rounded-lg p-3 mb-3">
              <Label className="overline-label">Recomendación final</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {RECOMMENDATIONS.map((r) => (
                  <button key={r.v} type="button"
                           onClick={() => setRecommendation(r.v)}
                           data-testid={`iv-rec-${r.v}`}
                           className={`px-3 py-1.5 text-xs font-bold rounded border transition ${
                             recommendation === r.v ? r.bg : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                           }`}>{r.l}</button>
                ))}
              </div>
              <Label className="overline-label mt-3 block">Comentarios finales</Label>
              <Textarea rows={2} value={comments}
                         onChange={(e) => setComments(e.target.value)}
                         placeholder="Fortalezas, banderas rojas, próximos pasos…"
                         data-testid="iv-comments" className="text-xs" />
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={save} disabled={!tpl || saving || !recommendation}
                   className="bg-indigo-800 hover:bg-indigo-900 gap-1.5"
                   data-testid="iv-save">
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Guardando…" : `Guardar entrevista (${overallScore}/100)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
